import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { industries, buildersIntro, buildersSides, buildersOutro } from "../../data/reportData";
import { X, Plus, Store, Settings2 } from "lucide-react";
import { CircledWord } from "../CircledWord";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

const PANEL_GREEN = "#00BA41";
const PANEL_BORDER = "rgba(0,0,0,0.25)";

function PromptCard({ quote }) {
  return (
    <div className="flex flex-col items-start gap-2.5 border px-7 py-9 md:px-10 md:py-10" style={{ borderColor: PANEL_BORDER }}>
      <span className="font-bold text-[28px] leading-none tracking-[-0.6px]" style={{ color: PANEL_GREEN }}>“</span>
      <p className="font-medium text-[16px] leading-[28px] tracking-[-0.6px] text-[#0A0A0A]">{quote.text}</p>
      <p className="font-semibold text-[16px] leading-[28px] tracking-[-0.6px]" style={{ color: PANEL_GREEN }}>
        {quote.who}, {quote.where}
      </p>
    </div>
  );
}

function IndustryPanel({ ind, onClose }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/45 z-[60]"
        data-testid="industry-overlay"
      />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[701px] bg-white z-[70] overflow-y-auto"
        data-testid="industry-panel"
      >
        <div className="flex flex-col items-start gap-8 p-6 sm:p-10">
          {/* header */}
          <div
            className="sticky top-0 z-10 -mt-6 sm:-mt-10 pt-6 sm:pt-10 bg-white self-stretch flex items-center gap-2.5 pb-5 border-b"
            style={{ borderColor: "rgba(10,10,10,0.25)" }}
          >
            <span className="flex-1 uppercase text-[15px] sm:text-[16px] leading-[16px] tracking-[1.98px]" style={{ color: PANEL_GREEN }}>
              {ind.num}. {ind.name}
            </span>
            <button onClick={onClose} className="shrink-0 text-[#0A0A0A] hover:opacity-60 transition-opacity" data-testid="industry-panel-close" aria-label="Close panel">
              <X size={24} />
            </button>
          </div>

          {/* heading */}
          <h3
            className="font-display font-semibold text-[#0A0A0A]"
            style={{ fontSize: "clamp(34px, 5.4vw, 50px)", lineHeight: 1.1, letterSpacing: "-1.3px" }}
          >
            {ind.tagline}
          </h3>

          {/* intro */}
          <p className="text-[16px] leading-[28px]" style={{ color: "rgba(10,10,10,0.85)" }}>{ind.intro}</p>

          {/* prompt cards */}
          {ind.quotes.map((q, i) => (
            <div key={i} className="self-stretch">
              <PromptCard quote={q} />
            </div>
          ))}

          {/* built in */}
          <div className="flex flex-col items-start gap-5">
            <p className="font-mono uppercase text-[16px] leading-[16px] tracking-[1.98px]" style={{ color: PANEL_GREEN }}>
              Built in
            </p>
            <ul className="flex flex-col gap-2">
              {ind.builtIn.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3.5 text-[16px] leading-[28px] tracking-[-0.2px] text-[#0A0A0A]"
                >
                  <span className="mt-[11px] w-[6px] h-[6px] rounded-full shrink-0" style={{ background: PANEL_GREEN }} />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.aside>
    </>
  );
}

export default function IndustriesSection() {
  const [openId, setOpenId] = useState(null);
  const [hoverSide, setHoverSide] = useState(null);
  const openInd = industries.find((i) => i.id === openId);

  const headerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: headerRef, offset: ["start end", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], ["-22%", "34%"]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1.15, 0.85]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section id="builders" className="section-gap" data-testid="section-builders">
      <div ref={headerRef} className="relative bg-black py-16 md:py-24 overflow-hidden" data-testid="builders-dark">
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute right-[-8%] top-0 w-[70%] h-[80%] z-0"
          style={{
            y: glowY,
            scale: glowScale,
            background: "radial-gradient(ellipse 50% 50% at 70% 30%, rgba(83,184,81,0.28) 0%, rgba(83,184,81,0.08) 40%, rgba(0,0,0,0) 72%)",
          }}
        />
        <motion.div className="relative z-10" style={{ y: contentY }}>
        <div className="report-col">
          <motion.p
            {...fadeUp}
            className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
            style={{ color: "#53B851" }}
          >
            The solution
          </motion.p>
          <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
            Building moved to the <CircledWord color="#53B851">operator</CircledWord>
          </motion.h2>
          <div className="space-y-6 mt-8">
            {buildersIntro.map((p, i) => (
              <motion.p key={i} {...fadeUp} className="body-text" style={{ color: "#C4C4C4", maxWidth: 800 }}>{p}</motion.p>
            ))}
          </div>

          <div className="flex flex-col md:flex-row mt-10 md:h-[240px]" data-testid="builders-sides">
            {buildersSides.map((s, i) => {
              const shrunk = hoverSide !== null && hoverSide !== i;
              const expanded = hoverSide === i;
              const dark = i === 0;
              const ink = dark ? "#0A0A0A" : "#FFFFFF";
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.12, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                  onMouseEnter={() => setHoverSide(i)}
                  onMouseLeave={() => setHoverSide(null)}
                  className={`relative p-8 md:p-10 overflow-hidden md:basis-0 md:min-w-0 md:h-full transition-[flex-grow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] cursor-default ${dark ? "bg-white" : "bg-[#53B851]"}`}
                  style={{ flexGrow: expanded ? 2.4 : shrunk ? 0.8 : 1, color: ink }}
                  data-testid={`builders-side-${i}`}
                >
                  {/* full content, fades out when shrunk */}
                  <div className={`transition-opacity duration-400 ${shrunk ? "md:opacity-0 md:pointer-events-none" : "opacity-100"}`}>
                    <div className="flex items-center gap-3">
                      {dark ? <Store size={18} style={{ color: ink }} className="shrink-0" /> : <Settings2 size={18} style={{ color: ink }} className="shrink-0" />}
                      <p className="uppercase text-[12px] md:text-[13px] tracking-[0.18em] font-medium whitespace-nowrap" style={{ color: ink }}>{s.label}</p>
                    </div>
                    <p className="font-display font-medium text-[21px] md:text-[25px] tracking-[-0.02em] leading-snug mt-6">
                      {s.title}
                    </p>
                    <p className="text-[14.5px] md:text-[15px] leading-relaxed mt-3" style={{ color: dark ? "#828282" : "rgba(255,255,255,0.85)" }}>{s.body}</p>
                  </div>
                  {/* shrunk hero: large icon + header */}
                  <div className={`hidden md:flex absolute inset-0 flex-col items-center justify-center gap-4 px-4 text-center transition-opacity duration-400 ${shrunk ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                    {dark ? <Store size={40} style={{ color: ink }} /> : <Settings2 size={40} style={{ color: ink }} />}
                    <p className="uppercase text-[10.5px] leading-[1.9] tracking-[0.18em] font-medium max-w-[120px]" style={{ color: ink }}>
                      {s.label}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.p {...fadeUp} className="body-text mt-10" style={{ color: "#C4C4C4", maxWidth: 800 }}>{buildersOutro}</motion.p>
        </div>
        </motion.div>

        {/* industry index list: infinite slow rotation */}
        <motion.div
          {...fadeUp}
          id="industries"
          className="report-col relative z-10 mt-16 md:mt-24"
          data-testid="industries-list"
        >
          <p className="text-[15px] md:text-[16px] text-[#E9E9E9] mb-5">
            Click an industry to read operator prompts, verbatim.
          </p>
          <div className={`industries-window relative overflow-hidden h-[540px] md:h-[680px] ${openInd ? "industries-paused" : ""}`} data-testid="industries-window">
            <div className="industries-track" data-testid="industries-track">
              {[0, 1].map((dup) => (
                <div key={dup} aria-hidden={dup === 1}>
                  {industries.map((ind) => (
                    <div key={`${dup}-${ind.id}`} id={dup === 0 ? ind.id : undefined} className="border-t border-white/15">
                      <button
                        onClick={() => setOpenId(ind.id)}
                        className="group w-full text-left py-7 md:py-8 flex items-baseline gap-5 md:gap-8 hover:bg-white/[0.04] transition-colors md:px-4 md:-mx-4"
                        data-testid={dup === 0 ? `industry-row-${ind.id}` : undefined}
                        tabIndex={dup === 0 ? 0 : -1}
                      >
                        <span className="text-[13px] shrink-0" style={{ color: "#53B851" }}>{ind.num}</span>
                        <span className="flex-1">
                          <span className="font-display font-medium text-[24px] md:text-[30px] tracking-[-0.02em] text-white leading-tight block group-hover:translate-x-1.5 transition-transform duration-300">
                            {ind.name}
                          </span>
                          <span className="text-[14px] md:text-[15px] text-[#9C9C9C] mt-1 block">{ind.tagline}</span>
                        </span>
                        <span className="shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="#53B851" aria-hidden="true">
                            <path d="M12 0c.9 6.8 4.3 10.2 12 12-7.7 1.8-11.1 5.2-12 12-.9-6.8-4.3-10.2-12-12C7.7 10.2 11.1 6.8 12 0Z" />
                          </svg>
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] z-10"
              style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 42%, rgba(0,0,0,0.95) 72%, #000 100%)" }}
            />
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {openInd && <IndustryPanel ind={openInd} onClose={() => setOpenId(null)} />}
      </AnimatePresence>
    </section>
  );
}
